import { useState } from "react";
import { Plus, Building2, Archive, ArchiveRestore, Pencil } from "lucide-react";
import { api } from "@/api/client";
import type { Client } from "@/api/types";
import { useWorkspaceId, useSession } from "@/app/session";
import { useClients, invalidateWorkspace } from "@/features/data";
import { Page } from "@/components/Page";
import { Button, Card, EmptyState, ErrorState, Field, IconButton, Input, Spinner } from "@/components/ui";
import { Dialog } from "@/components/Dialog";
import { useToast, errorMessage } from "@/components/Toast";
import { cn } from "@/lib/cn";

export function ClientsPage() {
  const wid = useWorkspaceId();
  const { workspace } = useSession();
  const isAdmin = workspace?.role !== "member";
  const { toast } = useToast();
  const { data: clients, isLoading, isError, refetch } = useClients(true);
  const [dialog, setDialog] = useState<{ client?: Client } | null>(null);

  async function archive(c: Client, archived: boolean) {
    try {
      await api.patch(`/workspaces/${wid}/clients/${c.id}`, { archived });
      invalidateWorkspace(wid);
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  return (
    <Page
      title="Clients"
      description="Each client bills in its own currency."
      actions={isAdmin && <Button size="sm" onClick={() => setDialog({})}><Plus className="size-4" /> New client</Button>}
    >
      {isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : (clients?.length ?? 0) === 0 ? (
        <EmptyState icon={<Building2 className="size-8" />} title="No clients yet" hint={isAdmin ? "Add a client to group projects and set a billing currency." : "No clients have been added yet."} />
      ) : (
        <Card>
          {clients!.map((c, i) => (
            <div key={c.id} className={cn("flex items-center gap-3 px-4 py-3", i < clients!.length - 1 && "border-b border-line", c.archived && "opacity-60")}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink">{c.name}</p>
                {c.note && <p className="truncate text-xs text-muted">{c.note}</p>}
              </div>
              <span className="mono rounded bg-elevated px-1.5 py-0.5 text-xs text-muted">{c.currency}</span>
              {isAdmin && (
                <>
                  <IconButton label="Edit" onClick={() => setDialog({ client: c })}><Pencil className="size-4" /></IconButton>
                  <IconButton label={c.archived ? "Unarchive" : "Archive"} onClick={() => archive(c, !c.archived)}>
                    {c.archived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
                  </IconButton>
                </>
              )}
            </div>
          ))}
        </Card>
      )}
      {dialog && <ClientDialog client={dialog.client} onClose={() => setDialog(null)} />}
    </Page>
  );
}

function ClientDialog({ client, onClose }: { client?: Client; onClose: () => void }) {
  const wid = useWorkspaceId();
  const { workspace } = useSession();
  const { toast } = useToast();
  const editing = !!client;
  const [name, setName] = useState(client?.name ?? "");
  const [note, setNote] = useState(client?.note ?? "");
  const [currency, setCurrency] = useState(client?.currency ?? workspace?.currency ?? "SEK");
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      const body = { name, note, currency: currency.toUpperCase() };
      if (editing) await api.patch(`/workspaces/${wid}/clients/${client!.id}`, body);
      else await api.post(`/workspaces/${wid}/clients`, body);
      invalidateWorkspace(wid);
      onClose();
    } catch (err) {
      toast(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={editing ? "Edit client" : "New client"}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="Eikland Øko AS" />
        </Field>
        <Field label="Currency" hint="ISO 4217, e.g. SEK, EUR, NOK.">
          <Input value={currency} onChange={(e) => setCurrency(e.target.value)} maxLength={3} className="w-28 uppercase" />
        </Field>
        <Field label="Note">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" loading={busy} disabled={!name}>{editing ? "Save" : "Create"}</Button>
        </div>
      </form>
    </Dialog>
  );
}
